/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import WhitelistAddressSearch from "./components/WhitelistAddressSearch";
import WhitelistAddressList from "./components/WhitelistAddressList";
import RequestNewWhitelistAddressModal from "./components/RequestNewWhitelistAddressModal";
import WhitelistAddressToolbar from "./components/WhitelistAddressToolbar";
import { Box } from "@chakra-ui/react";
import { FaPlus } from "react-icons/fa";
import SweetAlert2 from "react-sweetalert2";

interface Filters {
  [key: string]: any;
}

interface PayloadType {
  [key: string]: any;
}

const rowPerPage = 10;

const initialFilters: Filters = {
  merchant_id: "",
  asset: "",
  status: "",
};

const WhitelistAddresses = ({ userPermissions }: { userPermissions: any }) => {
  const [swalProps, setSwalProps] = useState<any>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFilterLoading, setIsFilterLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [lastKeys, setLastKeys] = useState<{ [key: number]: string }>({});
  const [currentLastKey, setCurrentLastKey] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(rowPerPage);
  const [rowCount, setRowCount] = useState(rowPerPage);
  const userRole = localStorage.getItem("userRole")?.toLowerCase();


  const [isOpenRequestNewModal, setIsOpenRequestNewModal] =
    useState<boolean>(false);

  const [assetList, setAssetList] = useState<any>(null);

  const [showFilters, setShowFilters] = useState(false);

  // AbortController for cancelling requests on unmount
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  // Cleanup: Cancel all pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Create new AbortController for each batch of requests
  const getAbortSignal = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  };

  useEffect(() => {
    const signal = getAbortSignal();
    const currentRequestId = ++requestIdRef.current;

    const fetchData = async () => {
      localStorage.removeItem("merchantIdToFilterWhitelistAddress");
      localStorage.removeItem("subaccountIdToFilterWhitelistAddress");

      setIsLoading(true);
      await fetchWhitelistAddresses({
        page: 0,
        limit: rowPerPage,
        lastkey: "",
        ...filters,
      }, false, signal, currentRequestId);
      if (signal.aborted || requestIdRef.current !== currentRequestId) return;
      if (userRole === "superadmin") {
        await fetchAssetsForSuperadmin(signal);
        if (signal.aborted || requestIdRef.current !== currentRequestId) return;
      } else {
        await fetchAssets(signal);
        if (signal.aborted || requestIdRef.current !== currentRequestId) return;
      }

      setIsLoading(false);
      setIsFilterLoading(false);
    };

    fetchData();
  }, []);

  const fetchWhitelistAddresses = async (
    payload: PayloadType = {},
    isReset = false,
    signal?: AbortSignal,
    requestId?: number
  ) => {
    try {
      const queryObject: PayloadType = {
        limit: payload?.limit,
        lastkey: payload?.lastkey
      };

      const merchantId = localStorage.getItem(
        "merchantIdToFilterWhitelistAddress"
      );
      const subaccountId = localStorage.getItem(
        "subaccountIdToFilterWhitelistAddress"
      );

      if (payload?.status !== "" && payload?.status !== undefined) {
        queryObject.status = payload?.status;
      }

      if (payload?.asset !== "" && payload?.asset !== undefined) {
        queryObject.asset = payload?.asset;
      }

      if (payload?.nickname !== "" && payload?.nickname !== undefined) {
        queryObject.nickname = payload?.nickname;
      }

      if (payload?.address !== "" && payload?.address !== undefined) {
        queryObject.address = payload?.address;
      }

      if (payload?.network !== "" && payload?.network !== undefined) {
        queryObject.network = payload?.network;
      }

      if (userRole === 'superadmin') {

        if (merchantId && merchantId !== "" && merchantId !== undefined) {
          queryObject.merchant_id = merchantId;
        }

        if (subaccountId && subaccountId !== "" && subaccountId !== undefined) {
          queryObject.subaccount_id = subaccountId;
        }
      }

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/trade/whitelist-addresses?${queryString}`,
        { signal }
      );

      // Don't update state if request was aborted
      if (signal?.aborted) return;
      if (requestId !== undefined && requestIdRef.current !== requestId) return;

      let newData = response.data.whitelist_addresses?.payload;

      if (newData && newData.length !== 0) {
        setTableData(newData);
      } else {
        setTableData([]);
      }

      // Manage Last Key and Total Record
      let newLastKey = response.data.whitelist_addresses?.lastkey;
      if (newLastKey) {
        setCurrentLastKey(newLastKey);
        let newPage = payload?.page;
        let newPageSize = payload?.limit;

        if (newData.length !== 0) {
          setRowCount((newPage + 1) * newPageSize + newPageSize);
        } else {
          setRowCount(newPage * newPageSize + newPageSize);
        }
        setLastKeys({ ...lastKeys, [newPage]: newLastKey });
      }
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      if (requestId !== undefined && requestIdRef.current !== requestId) return;
      setTableData([]);
      // Clear all
      setPage(0);
      setPageSize(0);
      setRowCount(0);
      setLastKeys([]);
      setCurrentLastKey("");
      console.error("Error fetching data", error);
    }
  };

  const fetchAssetsForSuperadmin = async (signal?: AbortSignal) => {
    try {
      let requestUrl = `/api/datasource/assets`;

      const response = await axios.get(
        process.env.REACT_APP_API_URL + requestUrl,
        { signal }
      );

      const data = response?.data;

      setAssetList(data);
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      console.error("Error fetching assets", error);
    }
  };

  const fetchAssets = async (signal?: AbortSignal) => {
    const subaccountId = localStorage.getItem("subaccountId");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/subaccount/get-subaccount-by-id?subaccountid=${subaccountId}`,
        { signal }
      );

      const accountData = response.data.subaccount?.payload;

      if (!accountData) {
        throw new Error('No subaccount data found');
      }

      const assetMap = new Map();

      accountData.account_methods.forEach((method: any) => {
        if (!assetMap.has(method.asset)) {
          assetMap.set(method.asset, {
            name: method.asset,
            decimal: method.decimal ?? 2,
            crypto: method.crypto
          });
        }
      });

      const uniqueAssets = Array.from(assetMap.values());
      setAssetList(uniqueAssets);
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      console.error("Error fetching subaccount details:", error);
    }
  };

  const setPaginationModel = async (newValue: any) => {
    setIsLoading(true);

    let newFilters: any = {
      page: newValue.page,
      limit: newValue.pageSize,
      lastkey: currentLastKey,
      ...filters
    };

    // If click prev pagination
    if (newValue.page < page) {
      newFilters = {
        ...newFilters,
        lastkey: newValue.page !== 0 ? lastKeys[newValue.page - 1] : "",
      };
    }

    // if select new row per page, reset page to 0, reset lastkey
    if (newValue.pageSize !== pageSize) {
      newFilters = {
        page: 0,
        limit: newValue.pageSize,
        lastkey: "",
        ...filters
      };
      setPage(0);
      setPageSize(newValue.pageSize);
      setCurrentLastKey("");
      setLastKeys([]);
    } else {
      setPage(newValue.page);
    }

    await fetchWhitelistAddresses(newFilters);
    setIsLoading(false);
  };

  const handleSearchSubmit = async () => {
    setIsLoading(true);
    setIsFilterLoading(true);
    setCurrentLastKey("");
    setLastKeys({});
    setPage(0);
    await fetchWhitelistAddresses({ page: 0, limit: rowPerPage, lastkey: "", ...filters });
    setIsLoading(false);
    setIsFilterLoading(false);
  };

  const handleSearchClear = async () => {
    localStorage.removeItem("merchantIdToFilterWhitelistAddress");
    localStorage.removeItem("subaccountIdToFilterWhitelistAddress");

    setIsLoading(true);
    setIsFilterLoading(true);
    setFilters(initialFilters);
    setPage(0);
    setCurrentLastKey("");
    setLastKeys([]);
    await fetchWhitelistAddresses(
      { page: 0, limit: rowPerPage, lastkey: "", ...initialFilters },
      true
    );
    setIsLoading(false);
    setIsFilterLoading(false);
  };

  const triggerReloadList = async () => {
    setIsLoading(true);
    await fetchWhitelistAddresses({
      page: page,
      limit: rowPerPage,
      lastkey: page !== 0 ? currentLastKey : "",
      ...filters,
    });
    setIsLoading(false);
  };

  const handleClickRequestNew = () => {
    showRequestNewModal();
  };

  const showRequestNewModal = () => {
    setIsOpenRequestNewModal(true);
  };

  const closeRequestNewModal = () => {
    setIsOpenRequestNewModal(false);
  };
  const triggerWhitelistAddressList = async () => {
    setIsLoading(true);
    setIsFilterLoading(true);
    await fetchWhitelistAddresses({
      page: page,
      limit: rowPerPage,
      lastkey: "",
      ...filters,
    });
    setIsLoading(false);
    setIsFilterLoading(false);
  };
  return (
    <>
      {userRole === "subaccount" &&
        userPermissions?.requestNewWhitelistAddress && (
          <>
            <RequestNewWhitelistAddressModal
              setSwalProps={setSwalProps}
              triggerReloadList={triggerReloadList}
              isOpen={isOpenRequestNewModal}
              onClose={() => closeRequestNewModal()}
            />
          </>
        )}
      <Box sx={{ position: "relative" }}>
        <WhitelistAddressToolbar
          userPermissions={userPermissions}
          isLoading={isLoading || isFilterLoading}
          onToggleFilters={() => setShowFilters((p) => !p)}
          onRefresh={triggerWhitelistAddressList}
          onNewRequest={handleClickRequestNew}
        />
        <WhitelistAddressSearch
          showFilters={showFilters}
          userRole={userRole}
          isFilterLoading={isFilterLoading}
          filters={filters}
          setFilters={setFilters}
          handleSearchSubmit={handleSearchSubmit}
          handleSearchClear={handleSearchClear}
          assetList={assetList}
        />
        <WhitelistAddressList
          userPermissions={userPermissions}
          isLoading={isLoading}
          page={page}
          pageSize={pageSize}
          rowCount={rowCount}
          tableData={isLoading ? [] : tableData}
          setPaginationModel={setPaginationModel}
          currentLastKey={currentLastKey}
          triggerReloadList={triggerReloadList}
        />
      </Box>

      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      ></SweetAlert2>
    </>
  );
};

export default WhitelistAddresses;